/**
 * The Today page's own events (IPD-2974).
 *
 * These measure how the page was used and go straight to PostHog. They do not replace anything the
 * backend emits: opening a piece of content and finishing a video already produce
 * `block_content_viewed` and `block_content_watched` there, because those change stored state. The
 * same act can raise one event in each tool — one records the fact, the other measures the screen.
 *
 * The names and the two view-model-free builders live in {@link today-event-names} and are
 * re-exported here, so this stays the one import the Today page needs. They are split off because
 * the screen that reports a finished task is a medical device entry point, and that keeps the tile
 * and face enums below out of its scope.
 */
import {
  type ProgramCatalogue,
  TodayAssessmentFace,
  TodayCardKind,
  TodayPromoKind,
  type TodayRow,
  TodayTasksFace,
  TodayTileKind,
  TodayUpcomingFace,
} from '@/ts/types/component/today-page';

/** A tile that always draws the same thing, so it has no face of its own to report. */
export const TODAY_TILE_STATIC = 'static';

/** One of the tile face enums, or the placeholder above for a tile that has no faces. */
export type TodayTileState =
  | TodayUpcomingFace
  | TodayTasksFace
  | TodayAssessmentFace
  | typeof TODAY_TILE_STATIC;

/**
 * The name each tile is reported under, as opposed to the identifier the code calls it by.
 *
 * Spelled out rather than sending the enum member, for three reasons that all bite later. Those
 * values are the frontend's own identifiers, so renaming one for clarity would silently rename it
 * in PostHog and orphan everything measured under the old name — the very thing the note on
 * {@link TodayEvent} argues against. Two of them are the same string, `TodayTileKind.taskList` and
 * `TodayTasksFace.taskList`, which would leave `tile` and `tile_state` indistinguishable by value
 * on the one tile where both appear. And that same one is misnamed for this purpose: the slot draws
 * four different things — the welcome video, the list, the celebration and a promo — so `taskList`
 * names one of its faces rather than the slot, which is what this property is for. `tasks` is the
 * subject the slot is about, and what the epic's own document calls it. The other seven already
 * name their slot.
 *
 * snake_case, like the property names and like the values the backend's own events carry.
 */
export const ANALYTICS_TILE_NAME: Record<TodayTileKind, string> = {
  [TodayTileKind.upcoming]: 'upcoming',
  [TodayTileKind.taskList]: 'tasks',
  [TodayTileKind.explore]: 'explore',
  [TodayTileKind.contentPrimary]: 'content_primary',
  [TodayTileKind.contentSecondary]: 'content_secondary',
  [TodayTileKind.assessment]: 'assessment',
  [TodayTileKind.journal]: 'journal',
  [TodayTileKind.programs]: 'programs',
};

/**
 * The name each face is reported under.
 *
 * The tasks tile's list face is `list` rather than `taskList`: the slot is already called `tasks`,
 * and spelling the list out again would say the same word twice. Read together they now say what
 * they mean — the tasks tile, in its list state, as opposed to its video, all-caught-up or promo
 * one.
 */
export const ANALYTICS_TILE_STATE_NAME: Record<TodayTileState, string> = {
  [TodayUpcomingFace.welcome]: 'welcome',
  [TodayUpcomingFace.recommendation]: 'recommendation',
  [TodayUpcomingFace.task]: 'task',
  [TodayUpcomingFace.nothingDue]: 'nothing_due',
  [TodayUpcomingFace.idle]: 'idle',
  [TodayUpcomingFace.suggestion]: 'suggestion',
  [TodayTasksFace.video]: 'video',
  [TodayTasksFace.taskList]: 'list',
  [TodayTasksFace.allCaughtUp]: 'all_caught_up',
  [TodayTasksFace.promo]: 'promo',
  [TodayAssessmentFace.available]: 'available',
  [TodayAssessmentFace.countdown]: 'countdown',
  [TodayAssessmentFace.locked]: 'locked',
  [TODAY_TILE_STATIC]: TODAY_TILE_STATIC,
};

/**
 * The name each category is reported under, as a promo and as a click target.
 *
 * The tasks tile's promo face is three different offers, and reporting them all as `promo` would
 * lose the only thing that face is asked about: whether the page sends anyone into a program.
 *
 * These are also the {@link TodayClick.targetType} of a click that goes off to browse one, next to
 * `session`, `content` and the rest: a category is a kind of destination like any other. What the
 * click MEANT rather than where it landed, and the difference is not academic — all three promos
 * currently lead to Explore, where two of the three categories are not (the medical one belongs on
 * Paths, and the non-medical programs do not launch until September), while the programs tile
 * already routes medical to Paths. Naming destinations would leave the two tiles reporting the
 * same act differently and need re-reading once the routes are fixed.
 *
 * Plural, because none of them advertises a particular thing: each offers a category. The copy
 * reads the same way — "Balm medical programs", "Prevention programs".
 */
export const ANALYTICS_PROMO_NAME: Record<TodayPromoKind, string> = {
  [TodayPromoKind.challenge]: 'challenges',
  [TodayPromoKind.medicalProgram]: 'medical_programs',
  [TodayPromoKind.preventionProgram]: 'prevention_programs',
};

/**
 * The same names, reached from the programs tile's own two catalogues.
 *
 * One vocabulary shared with the promos above, because both tiles do the same thing — invite the
 * user to go and browse a category — so an analyst asking how many clicks went looking for medical
 * programs does not have to know there are two places to ask it from. Which place it was is
 * already the `tile` property, and every one of these clicks is `cta_id: browse`, which is what
 * answers the same question across all the categories at once.
 */
export const ANALYTICS_CATALOGUE_NAME: Record<ProgramCatalogue, string> = {
  medical: ANALYTICS_PROMO_NAME[TodayPromoKind.medicalProgram],
  prevention: ANALYTICS_PROMO_NAME[TodayPromoKind.preventionProgram],
};

/**
 * Where a click leads, for the tiles whose controls open one of the payload's own items.
 *
 * The card kinds come from the payload; the three below them are the page's own destinations,
 * which no card kind covers: Explore is a shortcut with nothing behind it, a questionnaire opens
 * in a modal rather than a route, and the journal is written where it stands.
 */
export const ANALYTICS_TARGET_TYPE: Record<TodayCardKind, string> = {
  [TodayCardKind.session]: 'session',
  [TodayCardKind.homework]: 'homework',
  [TodayCardKind.challenge]: 'challenge',
  [TodayCardKind.assessment]: 'assessment',
  [TodayCardKind.program]: 'program',
  [TodayCardKind.content]: 'content',
};

/** Explore, which a card kind cannot name: it is a shortcut with no item behind it. */
export const TODAY_TARGET_EXPLORE = 'explore';

/** A questionnaire, which opens in a modal rather than at a route. */
export const TODAY_TARGET_QUESTIONNAIRE = 'questionnaire';

/** What was pressed and where it went, for {@link TodayEvent.tileClicked}. */
export interface TodayClick {
  /**
   * Which control inside the tile, since several have more than one: the Upcoming tile carries a
   * card, an onboarding CTA and a nudge towards Explore, and the tasks tile a row or a promo.
   */
  ctaId: string;
  targetType: string;
  /** Null for a destination with nothing to identify, such as Explore. */
  targetId: string | null;
  /** Only for a piece of content: what it is, and whether it was already marked read. */
  media?: string;
  alreadyRead?: boolean;
}

/** What the page has resolved about the tiles that draw more than one thing. */
export interface TodayTileFaces {
  upcoming: TodayUpcomingFace | null;
  tasks: TodayTasksFace | null;
  assessment: TodayAssessmentFace | null;
  /** Which of the three offers the tasks tile is rotating, when the promo face is the one it drew. */
  promo: TodayPromoKind | null;
}

/**
 * What a tile was drawing, as the two properties that describe it.
 *
 * Nothing is reported about the assessment tile's other cards, though it draws a carousel of them:
 * the backend leaves exactly one actionable and locks the rest, so the face already covers
 * everything the user could act on, and the click event carries the id of the one they opened. A
 * count of the cards would be a property with no question behind it, and adding one later does not
 * disturb what has already been measured.
 */
export interface TodayTileReport {
  state: TodayTileState;
  /** An axis below the state, where one exists. Null for every tile except the promo face. */
  variant: string | null;
}

/**
 * What to report about a tile the page has drawn.
 *
 * Only three tiles have faces. The rest always draw the same thing, and they report
 * {@link TODAY_TILE_STATIC} rather than nothing, so a query never has to account for its own gaps.
 * A face of null means the page could not resolve one, which is the same situation as having none.
 */
export function resolveTileReport(kind: TodayTileKind, faces: TodayTileFaces): TodayTileReport {
  const faceOf: Record<string, TodayTileState | null> = {
    [TodayTileKind.upcoming]: faces.upcoming,
    [TodayTileKind.taskList]: faces.tasks,
    [TodayTileKind.assessment]: faces.assessment,
  };
  const state = faceOf[kind] ?? TODAY_TILE_STATIC;
  const isPromo = kind === TodayTileKind.taskList && state === TodayTasksFace.promo;
  return {
    state,
    variant: isPromo && faces.promo ? ANALYTICS_PROMO_NAME[faces.promo] : null,
  };
}

/* eslint-disable @typescript-eslint/naming-convention -- these are property names as they are
   reported, in the same snake_case the backend's own events use. Renaming them here renames them
   in PostHog. */

/**
 * Which tiles the page drew, in the order they were drawn.
 *
 * Read off the rendered rows rather than the layout's own table, because tiles drop out: a licence
 * without assessments, or an organisation with Explore blocked, gets a shorter page, and the point
 * of the property is what that user was given. Without it there is no way to ask "of the people
 * who had this tile, how many saw it", because nothing would say who had it.
 */
export function renderedTiles(rows: TodayRow[]): string[] {
  return rows.flatMap(row => row.cells.map(cell => ANALYTICS_TILE_NAME[cell.kind]));
}

/**
 * How far down the page a tile was, counting from one, or null if the page did not draw it.
 *
 * One number over the whole page rather than a band and a place inside it. The question behind the
 * property is whether what sits low gets lost to scrolling, and a single ordinal answers exactly
 * that. A band name would answer it too, until the day the composition changes: reordering the
 * bands or adding one would leave old and new events carrying the same name for different places
 * and comparing as though nothing had happened. An ordinal shifts honestly instead — and it is
 * already implied by {@link renderedTiles}, which every tile event carries — and the tile events
 * now derive both from one list rather than from two traversals that happen to agree.
 *
 * It also survives the width. The bento is a container query that folds three columns into two and
 * then one, so a band is one visual row on a desktop and three stacked ones on a phone; reading
 * order does not change with it. On a single column this number IS the vertical order, which is
 * where being lost to scrolling matters most, and on wider ones it is reading order, with the
 * viewport size already on every event from PostHog itself if the difference ever matters.
 */
export function tilePositionIn(tiles: string[], kind: TodayTileKind): number | null {
  const position = tiles.indexOf(ANALYTICS_TILE_NAME[kind]);
  return position === -1 ? null : position + 1;
}

/** The same answer starting from the rows, for a caller that has not listed them yet. */
export function findTilePosition(rows: TodayRow[], kind: TodayTileKind): number | null {
  return tilePositionIn(renderedTiles(rows), kind);
}

/** The properties every tile event carries, spelled once for all of them. */
export function tileProperties(
  rows: TodayRow[],
  kind: TodayTileKind,
  report: TodayTileReport,
): Record<string, unknown> {
  // Once, and the position read off it: the doc above claims the two can never disagree, so they
  // are taken from the same list rather than from two traversals that happen to match.
  const tiles = renderedTiles(rows);

  return {
    tile: ANALYTICS_TILE_NAME[kind],
    tile_state: ANALYTICS_TILE_STATE_NAME[report.state],
    tile_state_variant: report.variant,
    // Before the position, which counts inside it. Carried on the tile events too, not only on the
    // visit: the page can be re-fetched inside one visit — answering the onboarding questionnaire
    // does exactly that, and the assessment tile appears where there was none — so the visit's list
    // can no longer describe the page a tile was seen on. With the list on the event itself,
    // nothing has to be joined to be read.
    tiles_rendered: tiles,
    tile_position: tilePositionIn(tiles, kind),
  };
}

/**
 * A piece of reading, as the content event reports it.
 *
 * `tile` says which of the two cells it sat in, from the same vocabulary the tile events use, so
 * the two cells can be compared without inventing a second name for them.
 */
export function contentProperties(kind: TodayTileKind, content: {
  id: string;
  media: string;
  read: boolean;
}): Record<string, unknown> {
  return {
    tile: ANALYTICS_TILE_NAME[kind],
    content_id: content.id,
    media: content.media,
    // Whether the card was already dimmed when it was put in front of the user again, which is
    // what tells an offer nobody took from one that had already been taken.
    already_read: content.read,
  };
}

/**
 * A journal entry that was saved, described without any of what it says.
 *
 * Neither the text nor the mood travels, and neither one is passed IN. The entry itself is a
 * personal health note and a self-reported mood about an identified person, and the question
 * behind the event — whether this page gets people journaling — is answered by two derived
 * values. Taking the whole draft and picking two fields out of it would work exactly as well
 * today, and would leave the defence one careless spread deep in a module whose every other
 * builder spreads.
 */
export function journalProperties(entry: {hasText: boolean; feelingsCount: number}): Record<string, unknown> {
  return {
    tile: ANALYTICS_TILE_NAME[TodayTileKind.journal],
    has_text: entry.hasText,
    feelings_count: entry.feelingsCount,
  };
}

/**
 * What a click carries on top of the tile it happened in.
 *
 * The two content fields are left out entirely rather than sent empty when the destination is not
 * a piece of content: a session click has no medium, and a property that is present-but-null on
 * most events reads as missing data rather than as not applicable.
 */
export function clickProperties(click: TodayClick): Record<string, unknown> {
  return {
    cta_id: click.ctaId,
    target_type: click.targetType,
    target_id: click.targetId,
    ...(click.media ? {media: click.media} : {}),
    ...(typeof click.alreadyRead === 'boolean' ? {already_read: click.alreadyRead} : {}),
  };
}

/* eslint-enable @typescript-eslint/naming-convention */

export {TodayEvent, commonProperties, taskProperties} from '@/ts/composables/pure/today-event-names';
